// Plan Deliverables Simple Component
// Shows package deliverables breakdown for calculator mock plan

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { PACKAGE_DELIVERABLES } from "@/lib/package-config";

interface PlanItem {
  id: string;
  careerStage: string;
  numberOfShows: number;
  numberOfArtists: number;
  showsPerArtist: number;
  artistsWithBaseShows?: number;
  artistsWithExtraShow?: number;
  packageTier: 'set-core' | 'set-audio' | 'set-video';
  pricePerShow: number;
  totalPrice: number;
}

interface PlanDeliverablesSimpleProps {
  planItems: PlanItem[];
}

// Transform deliverables from package-config into display format
const standardDeliverables = PACKAGE_DELIVERABLES.filter(item =>
  !item.category || item.category === 'standard'
);

const DELIVERABLES = {
  setCore: standardDeliverables
    .filter(item =>
      item.setCore.included || item.setAudio.included || item.setVideo.included
    )
    .map(item => ({
      name: item.name,
      included: item.setCore.included,
      note: item.setCore.note
    })),

  setAudio: standardDeliverables
    .filter(item =>
      item.setAudio.included || item.setVideo.included
    )
    .map(item => ({
      name: item.name,
      included: item.setAudio.included,
      note: item.setAudio.note
    })),

  setVideo: standardDeliverables.map(item => ({
    name: item.name,
    included: item.setVideo.included,
    note: item.setVideo.note
  }))
};

export function PlanDeliverablesSimple({ planItems }: PlanDeliverablesSimpleProps) {
  if (planItems.length === 0) {
    return null;
  }

  // Count shows for each package type
  const packageCounts = planItems.reduce(
    (acc, item) => {
      acc[item.packageTier] += item.numberOfShows;
      return acc;
    },
    { 'set-core': 0, 'set-audio': 0, 'set-video': 0 }
  );

  return (
    <Card className="border-pink-200/50 overflow-hidden shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Package Deliverables
          <span className="text-sm font-normal text-muted-foreground ml-2">
            (Included with selected shows)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* SET Core Package */}
          <div
            className={cn(
              "rounded-xl overflow-hidden transition-all duration-300",
              packageCounts['set-core'] > 0
                ? "border-2 border-transparent bg-gradient-to-r from-gray-400 to-gray-500 p-[2px] shadow-lg hover:shadow-xl hover:scale-[1.02]"
                : "border-2 border-gray-200 bg-gray-50/30 opacity-60"
            )}
          >
            <div className={cn(
              "h-full rounded-[10px] overflow-hidden",
              packageCounts['set-core'] > 0 ? "bg-white" : "bg-gray-50/30"
            )}>
              <div className="bg-gradient-to-br from-gray-500 to-gray-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg">SET Core</h3>
                    <p className="text-sm opacity-90">Essential Foundation</p>
                  </div>
                  <div className="w-3 h-3 rounded-full bg-white"></div>
                </div>
                {packageCounts['set-core'] > 0 && (
                  <div className="mt-2 text-sm font-medium">
                    {packageCounts['set-core']} {packageCounts['set-core'] === 1 ? 'show' : 'shows'} selected
                  </div>
                )}
              </div>

              <div className="p-4 space-y-3">
                {DELIVERABLES.setCore.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm hover:bg-gray-50/50 -mx-1 px-1 py-1 rounded transition-colors">
                    {item.included ? (
                      <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    ) : (
                      <X className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
                    )}
                    <div className={cn("flex-1", !item.included && "text-gray-400")}>
                      <div className={item.included ? "font-medium" : ""}>{item.name}</div>
                      {item.note && (
                        <div className="text-xs text-gray-500 mt-0.5">{item.note}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SET Audio Package */}
          <div
            className={cn(
              "rounded-xl overflow-hidden transition-all duration-300",
              packageCounts['set-audio'] > 0
                ? "border-2 border-transparent bg-gradient-to-r from-blue-400 to-blue-600 p-[2px] shadow-lg hover:shadow-xl hover:scale-[1.02]"
                : "border-2 border-gray-200 bg-gray-50/30 opacity-60"
            )}
          >
            <div className={cn(
              "h-full rounded-[10px] overflow-hidden",
              packageCounts['set-audio'] > 0 ? "bg-white" : "bg-gray-50/30"
            )}>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg">SET Audio</h3>
                    <p className="text-sm opacity-90">Audio-Enhanced Experience</p>
                  </div>
                  <div className="w-3 h-3 rounded-full bg-white"></div>
                </div>
                {packageCounts['set-audio'] > 0 && (
                  <div className="mt-2 text-sm font-medium">
                    {packageCounts['set-audio']} {packageCounts['set-audio'] === 1 ? 'show' : 'shows'} selected
                  </div>
                )}
              </div>

              <div className="p-4 space-y-3">
                {DELIVERABLES.setAudio.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm hover:bg-blue-50/50 -mx-1 px-1 py-1 rounded transition-colors">
                    {item.included ? (
                      <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    ) : (
                      <X className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
                    )}
                    <div className={cn("flex-1", !item.included && "text-gray-400")}>
                      <div className={item.included ? "font-medium" : ""}>{item.name}</div>
                      {item.note && (
                        <div className="text-xs text-blue-600 mt-0.5 font-medium">{item.note}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SET Video Package */}
          <div
            className={cn(
              "rounded-xl overflow-hidden transition-all duration-300",
              packageCounts['set-video'] > 0
                ? "border-2 border-transparent bg-gradient-to-r from-purple-400 to-purple-600 p-[2px] shadow-lg hover:shadow-xl hover:scale-[1.02]"
                : "border-2 border-gray-200 bg-gray-50/30 opacity-60"
            )}
          >
            <div className={cn(
              "h-full rounded-[10px] overflow-hidden",
              packageCounts['set-video'] > 0 ? "bg-white" : "bg-gray-50/30"
            )}>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg">SET Video</h3>
                    <p className="text-sm opacity-90">Video-Powered Partnership</p>
                  </div>
                  <div className="w-3 h-3 rounded-full bg-white"></div>
                </div>
                {packageCounts['set-video'] > 0 && (
                  <div className="mt-2 text-sm font-medium">
                    {packageCounts['set-video']} {packageCounts['set-video'] === 1 ? 'show' : 'shows'} selected
                  </div>
                )}
              </div>

              <div className="p-4 space-y-3">
                {DELIVERABLES.setVideo.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm hover:bg-purple-50/50 -mx-1 px-1 py-1 rounded transition-colors">
                    {item.included ? (
                      <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    ) : (
                      <X className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
                    )}
                    <div className={cn("flex-1", !item.included && "text-gray-400")}>
                      <div className={item.included ? "font-medium" : ""}>{item.name}</div>
                      {item.note && (
                        <div className="text-xs text-purple-600 mt-0.5 font-medium">{item.note}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Summary footer */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div className="group hover:scale-105 transition-transform duration-200">
              <div className="text-3xl font-bold text-gray-600">{packageCounts['set-core']}</div>
              <div className="text-muted-foreground font-medium">SET Core Shows</div>
            </div>
            <div className="group hover:scale-105 transition-transform duration-200">
              <div className="text-3xl font-bold text-blue-600">{packageCounts['set-audio']}</div>
              <div className="text-muted-foreground font-medium">SET Audio Shows</div>
            </div>
            <div className="group hover:scale-105 transition-transform duration-200">
              <div className="text-3xl font-bold text-purple-600">{packageCounts['set-video']}</div>
              <div className="text-muted-foreground font-medium">SET Video Shows</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
